/**
 * SO-3c — one spec sheet on the party's link, reading exactly what the Trade
 * PDF prints. Every field here comes out of the `sheet` dict the backend built
 * with routes/export's own `_spec_sheet_reading(clip, flags_for("trade"))`:
 * there is no field list in this file, and no price, vendor or internal note
 * can reach it because the payload never carries them.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { cacheBranding } from '@/utils/skin';
import { SiteOfficeHead, abs } from '@/pages/SiteOfficeSpecs';
import OfficeStrip, { parsePreview, withPreview } from '@/components/site-office/OfficeStrip';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const Fact = ({ label, value, testId }) => (
  <div data-testid={testId}><b>{label}</b><span>{value || '—'}</span></div>
);

export default function SiteOfficeSpecItem() {
  const { token, clipId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [office, setOffice] = useState(null);
  const [data, setData] = useState(null);
  const [gone, setGone] = useState('');
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const cat = params.get('cat') || '';
  // SO-9a2 — the firm reading this sheet from the preview.
  const preview = parsePreview(params.get('preview'));
  const lockedTitle = preview ? `Only ${office?.party?.name} can do this` : undefined;

  const load = useCallback(async () => {
    try {
      const [head, sheet] = await Promise.all([
        bare.get(`${API}/site-office-links/${token}`),
        bare.get(`${API}/site-office-links/${token}/specs/${clipId}`),
      ]);
      setOffice(head.data);
      if (head.data?.branding) cacheBranding(head.data.branding);
      setData(sheet.data);
    } catch (e) {
      setGone(e?.response?.status === 403
        ? 'That spec sheet is not part of your work'
        : 'This link is not valid');
    }
  }, [token, clipId]);

  useEffect(() => { load(); }, [load]);

  if (gone) {
    return (
      <main className="kso-page" data-testid="site-office-spec-item">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }
  if (!office || !data) {
    return (
      <main className="kso-page" data-testid="site-office-spec-item">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const s = data.sheet || {};
  const name = s.tag_name || s.title || 'Spec sheet';
  const pictures = data.images || [];
  const back = withPreview(`/site-office/${token}/specs${cat ? `?cat=${cat}` : ''}`, preview);

  const ask = async () => {
    const text = question.trim();
    if (!text) return;
    try {
      await bare.post(`${API}/site-office-links/${token}/messages`, {
        text: `Question on ${[s.spec_num, name].filter(Boolean).join(' — ')}: ${text}`,
      });
      toast.success(`Sent to ${office.firm_name}`);
      setQuestion(''); setAsking(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not send. Try again.');
    }
  };

  return (
    <main className="kso-page" data-testid="site-office-spec-item">
      <div className="kso-wrap">
        {preview ? (
          <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
            projectName={office.project?.name} partyName={office.party?.name} page="Spec sheet" />
        ) : null}
        <SiteOfficeHead data={office} />
        <div className="kso-crumb" data-testid="site-office-spec-crumb">
          <button type="button" className="back" data-testid="site-office-spec-back"
            onClick={() => navigate(back)}>‹ Spec sheets</button>
          <span>Site Office</span><span>›</span><span>Spec sheets</span><span>›</span>
          <b>{name}</b>
        </div>
        <div className="kso-grid">
          <section className="kso-card w12 kso-sheet">
            <div className="kso-card__bd">
              {pictures[0] ? (
                <a className="kso-ph" href={abs(pictures[0])} target="_blank" rel="noreferrer"
                  data-testid="site-office-spec-hero">
                  <img src={abs(pictures[0])} alt={name} />
                </a>
              ) : (
                <span className="kso-ph kso-ph--gone" data-testid="site-office-spec-hero-gone">
                  Picture no longer available
                </span>
              )}

              <div className="kso-sheet__head" data-testid="site-office-spec-head">
                <div className="n">{name}</div>
                {s.title && s.title !== name ? <div className="p">{s.title}</div> : null}
                <div className="m">
                  {[data.project?.name, data.project?.project_code, s.spec_num]
                    .filter(Boolean).join(' · ')}
                </div>
              </div>

              {(s.room || s.location) ? (
                <div className="kso-item__meta" data-testid="site-office-spec-where">
                  Goes in · {[s.room, s.location].filter(Boolean).join(' · ')}
                </div>
              ) : null}

              <div className="kso-sheet__facts" data-testid="site-office-spec-facts">
                <Fact label="Maker" value={s.manufacturer} testId="site-office-spec-fact-maker" />
                <Fact label="Model" value={s.model} testId="site-office-spec-fact-model" />
                <Fact label="Size" value={s.dimensions} testId="site-office-spec-fact-size" />
                <Fact label="Finish" value={s.finish} testId="site-office-spec-fact-finish" />
              </div>

              {s.trade_note ? (
                <div className="kso-sheet__note" data-testid="site-office-spec-note">
                  <b>Note for the trade</b>
                  <span>{s.trade_note}</span>
                </div>
              ) : null}

              {pictures.length > 1 ? (
                <div className="kso-photos" data-testid="site-office-spec-pictures">
                  {pictures.slice(1).map((url, i) => (
                    <figure className="kso-shot" key={url}>
                      <a className="kso-ph" href={abs(url)} target="_blank" rel="noreferrer"
                        data-testid={`site-office-spec-picture-${i}`}>
                        <img src={abs(url)} alt="" loading="lazy" />
                      </a>
                    </figure>
                  ))}
                </div>
              ) : null}

              {(data.pdf_files || []).length ? (
                <div data-testid="site-office-spec-pdfs">
                  <div className="ktm-group">Spec PDFs</div>
                  {(data.pdf_files || []).map((f, i) => (
                    <a className="kso-row link" key={f.url} href={abs(f.url)}
                      target="_blank" rel="noreferrer" data-testid={`site-office-spec-pdf-${i}`}>
                      <div className="t"><b>{f.name}</b></div>
                      <span className="chev">›</span>
                    </a>
                  ))}
                </div>
              ) : null}

              <div className="kso-sheet__actions" data-testid="site-office-spec-actions">
                <a className="kso-btn brand" data-testid="site-office-spec-pdf"
                  href={`${API}/site-office-links/${token}/specs/${clipId}/print.pdf`}
                  target="_blank" rel="noreferrer">Save PDF</a>
                <button type="button" className="kso-btn" data-testid="site-office-spec-question"
                  disabled={Boolean(preview)} title={lockedTitle}
                  onClick={() => setAsking((v) => !v)}>Question</button>
              </div>
              {asking ? (
                <div className="kso-compose">
                  <input className="kso-in" value={question}
                    placeholder={`Ask ${office.firm_name} about this sheet…`}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ask(); } }}
                    data-testid="site-office-spec-question-input" />
                  <button type="button" className="kso-btn brand" onClick={ask}
                    disabled={Boolean(preview)} title={lockedTitle}
                    data-testid="site-office-spec-question-send">Send</button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
        <div className="kso-foot">
          {office.firm_name} · this link is personal to {office.party?.name}
        </div>
      </div>
    </main>
  );
}
